/**
 * One section of the mapping screen: its title, how many fields are in it, and the rows.
 *
 * Every section can be folded away. Only "Confirmed" starts folded, since it is the one with nothing
 * left to do. "Matched automatically" stays open on purpose: those are the rows the confirm button
 * is about to turn into fields Waterlily reads, so they are the ones the advisor should see.
 */
import { useState, type ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';
import { Badge, Collapsible, CollapsibleContent, CollapsibleTrigger } from '@design-system';
import { fieldMapCopy } from '../lib/field-map-copy';
import type { FieldMapGroupKey } from '../lib/field-map-groups';

interface FieldMapGroupProps {
  group: FieldMapGroupKey;
  count: number;
  crmName: string;
  children: ReactNode;
}

const BADGE: Record<FieldMapGroupKey, 'warning' | 'default' | 'success' | 'secondary'> = {
  attention: 'warning',
  matched: 'default',
  confirmed: 'success',
  notMapped: 'secondary',
};

export function FieldMapGroup({ group, count, crmName, children }: FieldMapGroupProps) {
  const [open, setOpen] = useState(group !== 'confirmed');

  return (
    <Collapsible
      open={open}
      onOpenChange={setOpen}
      className="overflow-hidden rounded-lg border border-gray-200 bg-white"
    >
      <CollapsibleTrigger className="flex w-full items-center justify-between gap-4 bg-gray-50 px-4 py-3 text-left hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary">
        <span>
          <span className="flex items-center gap-2">
            <span className="text-base font-semibold text-gray-900">{fieldMapCopy.groupTitle(group)}</span>
            <Badge variant={BADGE[group]}>{count}</Badge>
          </span>
          <span className="mt-0.5 block text-sm text-gray-600">{fieldMapCopy.groupHint(group, crmName)}</span>
        </span>
        <span className="flex shrink-0 items-center gap-1 text-sm text-gray-600">
          {open ? fieldMapCopy.hideGroup : fieldMapCopy.showGroup}
          <ChevronDown className={`h-4 w-4 transition-transform ${open ? 'rotate-180' : ''}`} />
        </span>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <ul className="divide-y divide-gray-100 border-t border-gray-200">{children}</ul>
      </CollapsibleContent>
    </Collapsible>
  );
}
